import React, { useRef, useEffect, useState } from "react";

type Props = {
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  undo: () => void;
  redo: () => void;
};

const CanvasBoard = ({ canvasRef, undo, redo }: Props) => {
  const firstParentContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPanningRef = useRef(false); // Using ref to track panning state

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const MIN_ZOOM = 0.0001;
  const MAX_ZOOM = 1000;
  const ZOOM_SENSITIVITY = 0.0001;

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();

    if (event.ctrlKey || event.metaKey) {
      const container = containerRef.current;
      if (!container) return;

      const deltaZoom = -event.deltaY * ZOOM_SENSITIVITY;
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom + deltaZoom));

      setZoom(newZoom);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === " " || event.code === "Space") {
      isPanningRef.current = true;

      // if current cursor is not grabbing, mean moving, make it grab otherwise, don't change
      if (containerRef.current?.style.cursor !== "grabbing")
        updateCursor("grab");

      // if zoom key
    } else if (event.key === "Control" || event.key === "Meta") {
      updateCursor("zoom-in");
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === " " || event.code === "Space") {
      isPanningRef.current = false;

      updateCursor("auto");
    } else if (event.key === "Control" || event.key === "Meta") {
      updateCursor("auto");
    }
  };

  const handleMouseDown = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (isPanningRef.current) {
      const startX = event.pageX - pan.x;
      const startY = event.pageY - pan.y;

      const handleMouseMove = (e: MouseEvent) => {
        updateCursor("grabbing");
        setPan({
          x: e.pageX - startX,
          y: e.pageY - startY,
        });
      };

      const handleMouseUp = () => {
        updateCursor("grab");

        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
  };

  const updateCursor = (cursorType: string) => {
    const container = containerRef.current;
    if (container) container.style.cursor = cursorType;
    const firstParentContainer = firstParentContainerRef.current;
    if (firstParentContainer) firstParentContainer.style.cursor = cursorType;
  };

  useEffect(() => {
    const firstParentContainer = firstParentContainerRef.current;
    if (!firstParentContainer) return;

    firstParentContainer.addEventListener("wheel", handleWheel, {
      passive: false,
    });
    firstParentContainer.addEventListener("mousedown", handleMouseDown, {
      passive: false,
    });

    return () => {
      firstParentContainer.removeEventListener("wheel", handleWheel);
      firstParentContainer.removeEventListener("mousedown", handleMouseDown);
    };
  }, [zoom, pan]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, { passive: true });
    document.addEventListener("keyup", handleKeyUp, { passive: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.style.transform = `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`;
  }, [zoom, pan]);

  return (
    <div
      ref={firstParentContainerRef}
      className="relative flex h-full w-full flex-1 items-center justify-center overflow-hidden bg-borderColor"
    >
      <div
        ref={containerRef}
        id="canvas"
        className="flex min-h-[100vh] min-w-[100vw] items-center justify-center"
      >
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export default CanvasBoard;
